import React, { ReactNode, useEffect, useId, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';

// ---------- Modal ----------
interface ModalProps {
  open?: boolean;
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Disables closing (e.g. while an upload is running). */
  busy?: boolean;
}

const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

export const Modal: React.FC<ModalProps> = ({
  open = true,
  title,
  description,
  onClose,
  children,
  footer,
  size = 'md',
  busy = false,
}) => {
  const { t } = useLanguage();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  onCloseRef.current = onClose;
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyRef.current) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the first field if there is one, otherwise the dialog itself.
    const firstField = panelRef.current?.querySelector<HTMLElement>('input, textarea, select');
    (firstField || panelRef.current)?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-gray-950/50 backdrop-blur-[2px]"
        onClick={() => !busy && onClose()}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative flex max-h-[92vh] w-full ${sizes[size]} animate-fadeIn flex-col rounded-t-2xl bg-white shadow-xl outline-none dark:bg-gray-900 dark:ring-1 dark:ring-gray-800 sm:rounded-2xl`}
      >
        <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            {description && <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn btn-ghost btn-icon -mr-2 -mt-1 shrink-0"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 pb-6 pt-3">{children}</div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-6 py-4 dark:border-gray-800 sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Page header ----------
export const PageHeader: React.FC<{
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  leading?: ReactNode;
}> = ({ title, description, actions, leading }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div className="flex min-w-0 items-start gap-3">
      {leading}
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-[28px]">{title}</h1>
        {description && <div className="mt-1 max-w-2xl text-[15px] text-gray-500 dark:text-gray-400">{description}</div>}
      </div>
    </div>
    {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

// ---------- Empty state ----------
export const EmptyState: React.FC<{
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  text?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}> = ({ icon: Icon, title, text, action, compact }) => (
  <div className={`flex flex-col items-center text-center ${compact ? 'px-4 py-8' : 'px-6 py-14'}`}>
    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
      <Icon className="h-5 w-5" />
    </div>
    <p className="font-medium text-gray-900 dark:text-gray-100">{title}</p>
    {text && <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

// ---------- Loading ----------
export const Spinner: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <svg className={`animate-spin text-current ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export const LoadingState: React.FC<{ label?: string }> = ({ label }) => {
  const { t } = useLanguage();
  return (
    <div className="flex min-h-[40vh] items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400" role="status">
      <Spinner className="h-5 w-5 text-primary-600 dark:text-primary-400" />
      <span>{label || t('common.loading')}</span>
    </div>
  );
};

// ---------- Avatar ----------
export const Avatar: React.FC<{ name?: string | null; photoURL?: string | null; size?: 'sm' | 'md' | 'lg' }> = ({
  name,
  photoURL,
  size = 'md',
}) => {
  const dims = { sm: 'h-7 w-7 text-xs', md: 'h-9 w-9 text-sm', lg: 'h-14 w-14 text-lg' }[size];
  if (photoURL) {
    return <img src={photoURL} alt="" referrerPolicy="no-referrer" className={`${dims} shrink-0 rounded-full object-cover`} />;
  }
  const initials = (name || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
  return (
    <span
      className={`${dims} inline-flex shrink-0 items-center justify-center rounded-full bg-primary-100 font-semibold text-primary-800 dark:bg-primary-900 dark:text-primary-200`}
      aria-hidden="true"
    >
      {initials || '?'}
    </span>
  );
};

// ---------- Form field ----------
export const Field: React.FC<{ label: string; htmlFor: string; hint?: ReactNode; children: ReactNode }> = ({
  label,
  htmlFor,
  hint,
  children,
}) => (
  <div>
    <label htmlFor={htmlFor} className="field-label">
      {label}
    </label>
    {children}
    {hint && <p className="field-hint">{hint}</p>}
  </div>
);

export const ErrorNote: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div
    role="alert"
    className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
  >
    {children}
  </div>
);

// ---------- Project status badge ----------
export const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const { t } = useLanguage();
  if (!status || status === 'active') return null;
  return (
    <span className={`badge ${status === 'archived' ? 'badge-warn' : 'badge-danger'}`}>
      {t(`projects.status.${status}`)}
    </span>
  );
};
